import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { AbstractControl, FormControl, ReactiveFormsModule, Validators, type FormControl as FormControlType } from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { Store, select } from '@ngrx/store'
import { ACCOUNT_ACTIVE, AccountObject, AccountType, CarrierTypes, CompanyStatuses, Country, SERVICE_PROVIDER, User } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { chain, sortBy } from 'lodash-es'
import { Observable, Subject, combineLatest } from 'rxjs'
import { distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators'
import { loadAccounts, selectAllAccounts, type GenericOption } from 'src/app/store/accounts'
import { loadBusinessTypes, selectAllBusinessTypes } from 'src/app/store/business-types'
import { loadCarriers, selectAllCarriers } from 'src/app/store/carriers'
import { loadUsers, selectStaffUsers } from 'src/app/store/users'
import { EpochFieldModule } from 'src/components/epoch/epoch-field/epoch-field.module'
import { ReactiveAsteriskModule } from 'src/components/reactive-asterisk/reactive-asterisk.module'
import { SelectSearchModule } from 'src/components/select-search/select-search.module'
import { environment } from 'src/environments/environment'
import { locales } from 'src/pages/admin/settings/templates/form/locales'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { disableIf } from 'src/shared/utils/disable-if'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { CarrierFormGroup, CompanyBulkFormGroup, CompanyFormGroup, enableDisableCompanyForm } from '../company-form'
import { BuyerGroupsPickerComponent } from '../buyer-groups-picker/buyer-groups-picker.component'
import { loadCountries, selectAllCountries } from 'src/app/store/countries'

const { tradecafeAccount } = environment

@Component({
    selector: 'tc-company-details-tab',
    templateUrl: './company-details-tab.component.html',
    styleUrl: './company-details-tab.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        SelectSearchModule,
        ReactiveAsteriskModule,
        ReactiveFormsModule,
        FlexLayoutModule,
        MatRadioModule,
        MatCheckboxModule,
        MatSlideToggleModule,
        EpochFieldModule,
        BuyerGroupsPickerComponent,
    ]
})
export class CompanyDetailsTabComponent extends OnDestroyMixin implements OnInit, OnChanges {
  private readonly toaster = inject(ToasterService)
  private readonly store = inject(Store)


  // input
  @Input({ required: true }) readonly: boolean
  // Status edit is restricted to manager/admin/superuser (WA-21073). When false,
  // the Status control is disabled; its value still round-trips via getRawValue().
  @Input({ required: true }) canEditStatus: boolean
  @Input({ required: true }) company$: Observable<DeepReadonly<AccountObject>>
  @Input({ required: true }) companyForm: CompanyFormGroup
  @Input({ required: true }) carrierForm: CarrierFormGroup
  @Input({ required: true }) bulkForm: CompanyBulkFormGroup
  @Input({ required: true }) isBulkEdit: boolean
  @Input({ required: true }) selectedCompanies$: Observable<DeepReadonly<AccountObject[]>>

  protected readonly onCompanyTypeChange$ = new Subject<AccountType>()

  // derivatives
  protected companyAccount$: Observable<number>
  protected companyArchived$: Observable<boolean>
  protected managerUsersOfTradecafe$: Observable<DeepReadonly<User>[]>;
  protected managerOptions$: Observable<GenericOption[]>;
  protected managersOptions$: Observable<GenericOption[]>;
  protected parentOf$: Observable<string[]>;
  /** Read-only dropdown: synced from parentOf$, not bound to the form. */
  protected readonly parentOfControl = new FormControl<string[]>([]);
  /**
   * UI-only scalar adapter over `companyForm.controls.parents`, used by the
   * single-select "Subsidiary of" dropdown for non–service-provider accounts.
   * Keeps the form model as `parents: number[]` (canonical) while giving the
   * Angular Material / tc-select-search widget a plain-number binding.
   */
  protected readonly singleParentControl = new FormControl<number | null>(null);
  protected isServiceProvider$: Observable<boolean>;
  // ref data
  protected readonly locales = locales.map(([name, locale]) => ({ name, lang: locale }))
  protected readonly availableCompanyStatuses = CompanyStatuses
  protected readonly availableCarrierTypes = CarrierTypes
  protected readonly carriers$ = this.store.pipe(select(selectAllCarriers), waitNotEmpty())
  protected readonly companies$ = this.store.pipe(select(selectAllAccounts), waitNotEmpty())
  protected readonly businessTypes$ = this.store.pipe(select(selectAllBusinessTypes), waitNotEmpty())
  protected readonly countries$ = this.store.pipe(select(selectAllCountries), waitNotEmpty(), map(c => sortBy(c, 'name')))
  private readonly staffUsers$ = this.store.pipe(select(selectStaffUsers), waitNotEmpty())
  protected readonly logisticUsersOfTradecafe$ = this.staffUsers$.pipe(map(users =>
    users.filter(user =>user.role === 'logistics')))


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly'] || changes['canEditStatus']) {
      this.applyEditPermissions()
    }
    if (changes['readonly']) {
      const isSp = this.companyForm?.controls.type.value === SERVICE_PROVIDER
      disableIf(this.singleParentControl, this.readonly || isSp)
    }
  }

  ngOnInit(): void {
    this.store.dispatch(loadBusinessTypes())
    this.store.dispatch(loadCarriers({}))
    this.store.dispatch(loadAccounts({}))
    this.store.dispatch(loadUsers({}))
    this.store.dispatch(loadCountries())

    this.companyAccount$ = this.company$.pipe(map(company => company.account))
    this.companyArchived$ = this.company$.pipe(map(company => !!company.archived))

    this.applyEditPermissions()

    // When canEditManagers=true, fields should be ENABLED (so disable when false)
    replayForm(this.bulkForm.controls.canEditManagers).pipe(untilComponentDestroyed(this)).subscribe(canEditManagers => {
      disableIf(this.bulkForm.controls.accountManagersToAdd, !canEditManagers)
      disableIf(this.bulkForm.controls.accountManagersToDelete, !canEditManagers)
      disableIf(this.bulkForm.controls.accountManagersToReplace, !canEditManagers)
    })
    replayForm(this.bulkForm.controls.canEditManager).pipe(untilComponentDestroyed(this)).subscribe(canEditManager => {
      disableIf(this.bulkForm.controls.managerInCommon, !canEditManager)
    })
    replayForm(this.bulkForm.controls.canEditCoordinators).pipe(untilComponentDestroyed(this)).subscribe(canEditCoordinators => {
      disableIf(this.bulkForm.controls.coordinatorsToAdd, !canEditCoordinators)
      disableIf(this.bulkForm.controls.coordinatorsToDelete, !canEditCoordinators)
      disableIf(this.bulkForm.controls.coordinatorsToReplace, !canEditCoordinators)
    })
    replayForm(this.bulkForm.controls.canEditCoordinator).pipe(untilComponentDestroyed(this)).subscribe(canEditCoordinator => {
      disableIf(this.bulkForm.controls.coordinatorInCommon, !canEditCoordinator)
    })
    replayForm(this.bulkForm.controls.canEditLanguage).pipe(untilComponentDestroyed(this)).subscribe(canEditLanguage => {
      disableIf(this.bulkForm.controls.languageInCommon, !canEditLanguage)
    })

    replayForm(this.companyForm).pipe(untilComponentDestroyed(this)).subscribe(cf => {
      this.companyForm.controls.coordinator.clearValidators()
      this.companyForm.controls.manager.clearValidators()
      this.companyForm.controls.managers.clearValidators()
      if (cf.status === ACCOUNT_ACTIVE) {
        this.companyForm.controls.coordinator.addValidators(Validators.required)
        if (!this.companyForm.controls.manager.value?.length) this.companyForm.controls.manager.addValidators(Validators.required)
        if (!this.companyForm.controls.manager.value) this.companyForm.controls.managers.addValidators(Validators.required)
      }
    })

    // find common values
    combineLatest([this.company$, this.selectedCompanies$]).pipe(untilComponentDestroyed(this)).subscribe(([company, selectedCompanies]) => {
      this.bulkForm.patchValue(
        this.isBulkEdit ? {
          coordinatorInCommon: getCommonMember(selectedCompanies, c => c.coordinator),
          managerInCommon: getCommonMember(selectedCompanies, c => c.manager),
          languageInCommon: getCommonMember(selectedCompanies, c => c.language),
        } : {
          coordinatorInCommon: company.coordinator,
          managerInCommon: company.manager,
          languageInCommon: company.language,
        }
      )
    })

    // supplier and buyer account managers are traders
    // service provider account managers are logistics
    this.managerOptions$ = this.selectManagerOptions(this.companyForm.controls.manager)
    this.managersOptions$ = this.selectManagerOptions(this.companyForm.controls.managers)
    this.managerUsersOfTradecafe$ = this.company$.pipe(
      switchMap(company => this.staffUsers$.pipe(
        map(users => filterUsersByCompanyType(company.type, users))
      ))
    );
    // reset account managers when expected account manager role has changed
    this.onCompanyTypeChange$.pipe(
      map(expectedManagerRole),
      distinctUntilChanged(),
      untilComponentDestroyed(this),
    ).subscribe(() => {
      this.companyForm.controls.manager.reset()
      this.companyForm.controls.managers.reset()
    })

    this.onCompanyTypeChange$.pipe(untilComponentDestroyed(this)).subscribe(type => {
      if (type !== SERVICE_PROVIDER) {
        this.companyForm.controls.isPaps.setValue(false, { emitEvent: false })
        const currentParents = this.companyForm.controls.parents.value ?? []
        if (currentParents.length > 1) {
          this.companyForm.controls.parents.setValue([currentParents[0]])
          this.toaster.warning('Buyers and suppliers can only have one parent — extra parents were removed.')
        }
      }
    })


    this.parentOf$ = combineLatest([this.companies$, this.companyAccount$]).pipe(
      map(([companies, account]) =>
        companies
          .filter(c => (c.parents?.length ? c.parents.includes(account) : c.parent === account))
          .map(c => c.name)
      ),
      shareReplay(1)
    );
    this.parentOf$.pipe(untilComponentDestroyed(this)).subscribe(names => {
      const list = names ?? []
      this.parentOfControl.setValue(list, { emitEvent: false })
      list.length ? this.parentOfControl.enable({ emitEvent: false }) : this.parentOfControl.disable({ emitEvent: false })
    })

    // Wire the scalar "Subsidiary of" single-select (used for non-SP accounts)
    // to the canonical `parents: number[]` form control.
    this.isServiceProvider$ = replayForm(this.companyForm.controls.type).pipe(
      map(type => type === SERVICE_PROVIDER),
      distinctUntilChanged(),
      shareReplay(1),
    )
    replayForm(this.companyForm.controls.parents).pipe(untilComponentDestroyed(this)).subscribe(parents => {
      const first = parents?.[0] ?? null
      if (this.singleParentControl.value !== first) {
        this.singleParentControl.setValue(first, { emitEvent: false })
      }
    })
    this.singleParentControl.valueChanges.pipe(untilComponentDestroyed(this)).subscribe(value => {
      const next = value ? [value] : []
      const current = this.companyForm.controls.parents.value ?? []
      if (current.length !== next.length || current[0] !== next[0]) {
        this.companyForm.controls.parents.setValue(next)
      }
    })
    this.isServiceProvider$.pipe(untilComponentDestroyed(this)).subscribe(isSp => {
      // Keep the single-select disabled for SPs so a user can't edit it while
      // the multi-select is the authoritative UI.
      disableIf(this.singleParentControl, this.readonly || isSp)
    })
  }

  protected onCurrentCarrierTypeChange() {
    this.companyAccount$.pipe(take(1)).subscribe(account => {
      const { type, carrier_id } = this.carrierForm.getRawValue()
      const { name } = this.companyForm.getRawValue()
      if (carrier_id && !type?.length) {
        this.toaster.warning(`Carrier capabilities of ${name || account} will be disabled.`)
      }
    })
  }

  // AI-37: trader/logistics (readonly) can view DETAILS but not change it.
  // Status stays restricted to manager/admin/superuser on top of that (WA-21073).
  // carrier_id stays enabled so carrierForm never becomes DISABLED (valid=false).
  private applyEditPermissions() {
    if (!this.companyForm) return
    enableDisableCompanyForm(this.companyForm, this.readonly)
    disableIf(this.companyForm.controls.status, this.readonly || !this.canEditStatus)
    if (this.carrierForm) {
      disableIf(this.carrierForm.controls.type, this.readonly)
      disableIf(this.carrierForm.controls.operating_countries, this.readonly)
    }
  }

  protected onCountryChange(countries: Country[]) {
    this.carrierForm.controls.operating_countries.setValue(countries.map(p => p.code))
  }

  private selectManagerOptions(selectedManager: FormControlType<string | readonly string[]>) {
    return combineLatest([
      this.staffUsers$,
      replayForm(this.companyForm.controls.type),
      replayForm(selectedManager),
    ]).pipe(
      map(([staffUsers, type, selectedManager]) => {
        const expectedRole = expectedManagerRole(type)
        return staffUsers
          .filter(user =>
            user.role === expectedRole && !user.archived ||
            selectedManager?.includes(user.user_id))
          .map(({ archived, fullname, role, user_id }) => {
            const issue = archived ? `archived ${role}` : role !== expectedRole ? `${role}` : ''
            const name = `${issue ? '⚠️ ' : ''} ${fullname}`
            return { id: user_id, name, hint: issue }
          })
      }),
      shareReplay(1))
  }


  protected checkForErrorsIn(formControl: AbstractControl, name: string): string {
    if (formControl.hasError('required')) return `${name} is required`
    if (formControl.hasError('pattern') || formControl.hasError('invalidEmail')) return `${name} is not valid`
    if (formControl.hasError('pattern') || formControl.hasError('invalidPhone')) return `${name} is not valid`
    return ''
  }
}

function getCommonMember<T>(sourceArray: DeepReadonly<T[]>, accessor: (t: T) => any) {
  if (!Array.isArray(sourceArray)) {
    return null;
  }

  const uniqueValues = chain(sourceArray)
    .compact()
    .map(accessor)
    .uniq()
    .value();

  return uniqueValues.length === 1 ? uniqueValues[0] : null;
}

function filterUsersByCompanyType<T extends DeepReadonly<User>>(type: AccountType, users: readonly T[]): T[] {
  const expectedRole = expectedManagerRole(type)
  return users.filter(user => !user.archived && user.role === expectedRole);
}

function expectedManagerRole(type: AccountType): string {
  return type === SERVICE_PROVIDER ? 'logistics' : 'trader'
}
